// Auth context — fetches /api/me once on mount, exposes signed-in actor
// to the whole app via useMe(). Cloudflare Access already gates the page,
// so by the time React mounts, the user IS authenticated; we just need to
// know who they are + their role for owner-only UI.

import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { api } from './api';
import type { Me } from './types';

interface AuthState {
  me: Me | null;
  loading: boolean;
  error: string | null;
}

const AuthContext = createContext<AuthState>({
  me: null,
  loading: true,
  error: null,
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ me: null, loading: true, error: null });

  useEffect(() => {
    (async () => {
      const r = await api.me();
      if (r.ok) {
        setState({ me: r.data, loading: false, error: null });
      } else {
        setState({
          me: null,
          loading: false,
          error: `${r.error.code}: ${r.error.message}`,
        });
      }
    })();
  }, []);

  return <AuthContext.Provider value={state}>{children}</AuthContext.Provider>;
}

export function useMe(): AuthState {
  return useContext(AuthContext);
}
